import React, { useState, useEffect, useCallback } from "react";
import { Download, FileText, Calculator } from "lucide-react";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import PropTypes from "prop-types";

const TaxComputationPDF = ({ pan, assessmentYear, regime, userData }) => {
  const [formData, setFormData] = useState({
    // Basic Info
    pan: pan || "",
    name: userData?.name || "",
    aadhaar: userData?.aadhaar || "",
    financialYear: assessmentYear ?
      `${assessmentYear.split("-")[0]}-${parseInt(assessmentYear.split("-")[0]) + 1}` : "2024-25",
    bankAccount: userData?.bankAccount || "",
    assessmentYear: assessmentYear || "2025-26",
    ifsc: userData?.ifsc || "",
    taxRegime: regime === "old" ? "Old Tax Regime" : "New Tax Regime",

    // Section visibility
    showSalary: true,
    showHouseProperty: false,
    showCapitalGain: false,
    showBusiness: false,
    showOtherSources: true,

    // INCOME FROM SALARY
    grossSalary: userData?.incomeFromSalary?.grossSalary || 0,
    hra: userData?.incomeFromSalary?.hra || 0,
    travelAllowance: userData?.incomeFromSalary?.travelAllowance || 0,
    uniformAllowance: userData?.incomeFromSalary?.uniformAllowance || 0,
    otherAllowance: userData?.incomeFromSalary?.otherAllowance || 0,
    standardDeduction: userData?.incomeFromSalary?.standardDeduction || 50000,
    professionalTax: userData?.incomeFromSalary?.professionalTax || 0,

    // INCOME FROM HOUSE PROPERTY
    grossAnnualValue: userData?.incomeFromHouseProperty?.grossAnnualValue || 0,
    propertyTax: userData?.incomeFromHouseProperty?.propertyTax || 0,
    interestSelfOccupied: userData?.incomeFromHouseProperty?.interestSelfOccupied || 0,
    interestLetOut: userData?.incomeFromHouseProperty?.interestLetOut || 0,

    // INCOME FROM CAPITAL GAIN
    ltcgSales: userData?.incomeFromCapitalGain?.ltcgSales || 0,
    ltcgTransferExp: userData?.incomeFromCapitalGain?.ltcgTransferExp || 0,
    ltcgCostAcquisition: userData?.incomeFromCapitalGain?.ltcgCostAcquisition || 0,
    stcgSales: userData?.incomeFromCapitalGain?.stcgSales || 0,
    stcgTransferExp: userData?.incomeFromCapitalGain?.stcgTransferExp || 0,
    stcgCostAcquisition: userData?.incomeFromCapitalGain?.stcgCostAcquisition || 0,

    // INCOME FROM BUSINESS
    grossTurnover: userData?.incomeFromBusiness?.grossTurnover || 0,
    profitPercentage: userData?.incomeFromBusiness?.profitPercentage || 8,

    // INCOME FROM OTHER SOURCES
    savingsBankInterest: userData?.incomeFromOtherSources?.savingsBankInterest || 0,
    termDepositInterest: userData?.incomeFromOtherSources?.termDepositInterest || 0,
    nscInterest: userData?.incomeFromOtherSources?.nscInterest || 0,
    dividend: userData?.incomeFromOtherSources?.dividend || 0,
    otherIncome: userData?.incomeFromOtherSources?.otherIncome || 0,

    // DEDUCTIONS
    lifeInsurance: userData?.deductions?.lifeInsurance || 0,
    providentFund: userData?.deductions?.providentFund || 0,
    ppf: userData?.deductions?.ppf || 0,
    housingLoanPrincipal: userData?.deductions?.housingLoanPrincipal || 0,
    nscInvestment: userData?.deductions?.nscInvestment || 0,
    annuityPlan: userData?.deductions?.annuityPlan || 0,
    pensionScheme: userData?.deductions?.pensionScheme || 0,
    npsContribution: userData?.deductions?.npsContribution || 0,
    npsEmployer: userData?.deductions?.npsEmployer || 0,
    medicalInsurance: userData?.deductions?.medicalInsurance || 0,
    disabledDependent: userData?.deductions?.disabledDependent || 0,
    specifiedDisease: userData?.deductions?.specifiedDisease || 0,
    donation: userData?.deductions?.donation || 0,
    houseRent: userData?.deductions?.houseRent || 0,
    savingsInterest: userData?.deductions?.savingsInterest || 0,
    disability: userData?.deductions?.disability || 0,
    otherDeduction: userData?.deductions?.otherDeduction || 0,

    // Tax Calculation
    tds: userData?.taxDetails?.tds || 0,
    tcs: userData?.taxDetails?.tcs || 0,
  });

  const [isGenerating, setIsGenerating] = useState(false);
  const [loading, setLoading] = useState(false);

  // Optional: Fetch additional data if needed
  useEffect(() => {
    const fetchAdditionalData = async () => {
      if (!pan) return;

      try {
        setLoading(true);
        const docRef = doc(db, "itrWorking", pan);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          const yearData = data[formData.assessmentYear] || {};

          setFormData(prev => ({
            ...prev,
            // Update only fields not provided via props
            ...(yearData.taxCalculation && {
              standardDeduction: yearData.taxCalculation.standardDeduction || prev.standardDeduction,
              professionalTax: yearData.taxCalculation.professionalTax || prev.professionalTax,
              tds: yearData.taxCalculation.tdsTcsAmount || prev.tds,
            }),
            // Add other specific updates if needed
          }));
        }
      } catch (error) {
        console.error("Error fetching additional data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAdditionalData();
  }, [pan, formData.assessmentYear]);

  // Calculation functions
  const calculateSalaryIncome = useCallback(() => {
    if (!formData.showSalary) return 0;
    return Math.max(0, formData.grossSalary - formData.hra - formData.travelAllowance -
      formData.uniformAllowance - formData.otherAllowance -
      formData.standardDeduction - formData.professionalTax);
  }, [formData]);

  const calculateHousePropertyIncome = useCallback(() => {
    if (!formData.showHouseProperty) return 0;
    const standardDed = formData.grossAnnualValue * 0.3;
    return Math.max(0, formData.grossAnnualValue - standardDed - formData.propertyTax -
      formData.interestSelfOccupied - formData.interestLetOut);
  }, [formData]);

  const calculateLTCG = useCallback(() => {
    return Math.max(0, formData.ltcgSales - formData.ltcgTransferExp - formData.ltcgCostAcquisition);
  }, [formData]);

  const calculateSTCG = useCallback(() => {
    return Math.max(0, formData.stcgSales - formData.stcgTransferExp - formData.stcgCostAcquisition);
  }, [formData]);

  const calculateCapitalGain = useCallback(() => {
    if (!formData.showCapitalGain) return 0;
    return calculateLTCG() + calculateSTCG();
  }, [formData, calculateLTCG, calculateSTCG]);

  const calculateBusinessIncome = useCallback(() => {
    if (!formData.showBusiness) return 0;
    return (formData.grossTurnover * formData.profitPercentage) / 100;
  }, [formData]);

  const calculateOtherSources = useCallback(() => {
    if (!formData.showOtherSources) return 0;
    return formData.savingsBankInterest + formData.termDepositInterest +
      formData.nscInterest + formData.dividend + formData.otherIncome;
  }, [formData]);

  const calculateGrossTotalIncome = useCallback(() => {
    return calculateSalaryIncome() + calculateHousePropertyIncome() +
      calculateCapitalGain() + calculateBusinessIncome() + calculateOtherSources();
  }, [calculateSalaryIncome, calculateHousePropertyIncome, calculateCapitalGain, calculateBusinessIncome, calculateOtherSources]);

  const calculateSection80CDeductions = useCallback(() => {
    const isNewRegime = formData.taxRegime === "New Tax Regime";
    if (isNewRegime) return 0;

    return Math.min(150000,
      formData.lifeInsurance + formData.providentFund + formData.ppf +
      formData.housingLoanPrincipal + formData.nscInvestment + formData.annuityPlan +
      formData.pensionScheme + formData.npsContribution + formData.npsEmployer
    );
  }, [formData]);

  const calculateOtherDeductions = useCallback(() => {
    const isNewRegime = formData.taxRegime === "New Tax Regime";
    if (isNewRegime) return 0;

    return formData.medicalInsurance + formData.disabledDependent + formData.specifiedDisease +
      formData.donation + formData.houseRent + formData.savingsInterest +
      formData.disability + formData.otherDeduction;
  }, [formData]);

  const calculateTotalDeductions = useCallback(() => {
    return calculateSection80CDeductions() + calculateOtherDeductions();
  }, [calculateSection80CDeductions, calculateOtherDeductions]);

  const calculateTaxableIncome = useCallback(() => {
    return Math.max(0, calculateGrossTotalIncome() - calculateTotalDeductions());
  }, [calculateGrossTotalIncome, calculateTotalDeductions]);

  const calculateTax = useCallback(() => {
    const taxableIncome = calculateTaxableIncome();
    const isNewRegime = formData.taxRegime === "New Tax Regime";
    let tax = 0;

    if (isNewRegime) {
      // New Tax Regime 2024-25
      if (taxableIncome <= 300000) {
        tax = 0;
      } else if (taxableIncome <= 700000) {
        tax = (taxableIncome - 300000) * 0.05;
      } else if (taxableIncome <= 1000000) {
        tax = 20000 + (taxableIncome - 700000) * 0.10;
      } else if (taxableIncome <= 1200000) {
        tax = 50000 + (taxableIncome - 1000000) * 0.15;
      } else if (taxableIncome <= 1500000) {
        tax = 80000 + (taxableIncome - 1200000) * 0.20;
      } else {
        tax = 140000 + (taxableIncome - 1500000) * 0.30;
      }
    } else {
      // Old Tax Regime
      if (taxableIncome <= 250000) {
        tax = 0;
      } else if (taxableIncome <= 500000) {
        tax = (taxableIncome - 250000) * 0.05;
      } else if (taxableIncome <= 1000000) {
        tax = 12500 + (taxableIncome - 500000) * 0.2;
      } else {
        tax = 112500 + (taxableIncome - 1000000) * 0.3;
      }
    }

    return Math.round(tax);
  }, [calculateTaxableIncome, formData.taxRegime]);

  const calculateRebateU87A = useCallback(() => {
    const taxableIncome = calculateTaxableIncome();
    const tax = calculateTax();
    const isNewRegime = formData.taxRegime === "New Tax Regime";

    if (isNewRegime && taxableIncome <= 700000) {
      return Math.min(25000, tax);
    } else if (!isNewRegime && taxableIncome <= 500000) {
      return Math.min(12500, tax);
    }
    return 0;
  }, [calculateTaxableIncome, calculateTax, formData.taxRegime]);

  const calculateEducationCess = useCallback(() => {
    const taxAfterRebate = calculateTax() - calculateRebateU87A();
    return Math.round(taxAfterRebate * 0.04);
  }, [calculateTax, calculateRebateU87A]);

  const calculateNetTaxLiability = useCallback(() => {
    const tax = calculateTax();
    const rebate = calculateRebateU87A();
    const cess = calculateEducationCess();
    const totalTax = tax - rebate + cess;
    return Math.round(totalTax - formData.tds - formData.tcs);
  }, [calculateTax, calculateRebateU87A, calculateEducationCess, formData.tds, formData.tcs]);

  const generatePDF = async () => {
    setIsGenerating(true);
    try {
      const printWindow = window.open("", "_blank");
      const pdfContent = document.getElementById("pdf-content");

      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Tax Computation - ${formData.pan}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: Arial, sans-serif; font-size: 12px; line-height: 1.4; }
            .container { max-width: 210mm; margin: 0 auto; padding: 10mm; }
            /* Keep all other styles from your original code */
          </style>
        </head>
        <body>
          ${pdfContent.innerHTML}
        </body>
        </html>
      `);

      printWindow.document.close();
      setTimeout(() => {
        printWindow.print();
      }, 500);
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Error generating PDF. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 p-4 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-lg font-medium text-gray-700">Loading tax data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-4">
      <div className="max-width-8xl mx-auto">
        {/* Quick Input Form */}
        <div className="bg-white p-6 rounded-lg shadow-lg mb-6">
          <h2 className="text-2xl font-bold mb-6 text-blue-600 flex items-center gap-2">
            <Calculator className="w-6 h-6" />
            Quick Tax Computation Setup
          </h2>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <input
              type="text"
              placeholder="PAN Number"
              value={formData.pan}
              onChange={(e) => handleInputChange("pan", e.target.value)}
              className="border border-gray-300 p-2 rounded focus:ring-2 focus:ring-blue-500"
            />
            <input
              type="text"
              placeholder="Full Name"
              value={formData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              className="border border-gray-300 p-2 rounded focus:ring-2 focus:ring-blue-500"
            />
            <select
              value={formData.taxRegime}
              onChange={(e) => handleInputChange("taxRegime", e.target.value)}
              className="border border-gray-300 p-2 rounded focus:ring-2 focus:ring-blue-500"
            >
              <option value="Old Tax Regime">Old Tax Regime</option>
              <option value="New Tax Regime">New Tax Regime</option>
            </select>
            <input
              type="text"
              placeholder="Assessment Year"
              value={formData.assessmentYear}
              onChange={(e) => handleInputChange("assessmentYear", e.target.value)}
              className="border border-gray-300 p-2 rounded focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <input
              type="number"
              placeholder="Gross Salary"
              value={formData.grossSalary || ""}
              onChange={(e) => handleInputChange("grossSalary", Number(e.target.value) || 0)}
              className="border border-gray-300 p-2 rounded focus:ring-2 focus:ring-blue-500"
            />
            <input
              type="number"
              placeholder="HRA Received"
              value={formData.hra || ""}
              onChange={(e) => handleInputChange("hra", Number(e.target.value) || 0)}
              className="border border-gray-300 p-2 rounded focus:ring-2 focus:ring-blue-500"
            />
            <input
              type="number"
              placeholder="TDS Deducted"
              value={formData.tds || ""}
              onChange={(e) => handleInputChange("tds", Number(e.target.value) || 0)}
              className="border border-gray-300 p-2 rounded focus:ring-2 focus:ring-blue-500"
            />
            <input
              type="number"
              placeholder="Life Insurance"
              value={formData.lifeInsurance || ""}
              onChange={(e) => handleInputChange("lifeInsurance", Number(e.target.value) || 0)}
              className="border border-gray-300 p-2 rounded focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex justify-center">
            <button
              onClick={generatePDF}
              disabled={isGenerating}
              className={`px-8 py-3 rounded-lg font-semibold text-white transition-all flex items-center gap-2 ${isGenerating
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-700 hover:shadow-lg"
              }`}
            >
              {isGenerating ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  Generating PDF...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" />
                  Generate Tax Computation PDF
                </>
              )}
            </button>
          </div>
        </div>

        {/* PDF Preview */}
        <div id="pdf-content" className="bg-white rounded-lg shadow-lg" style={{ fontFamily: "Arial, sans-serif" }}>
          <div className="container" style={{ maxWidth: "794px", margin: "0 auto", padding: "20px" }}>

            {/* Header */}
            <div className="header" style={{ border: "2px solid #2563eb", padding: "15px", marginBottom: "20px", textAlign: "center", borderRadius: "8px" }}>
              <div className="company-name" style={{ fontSize: "24px", fontWeight: "bold", color: "#2563eb", marginBottom: "5px" }}>
                TAX SARTHI
              </div>
              <div className="tagline" style={{ fontSize: "18px", color: "#3b82f6", marginBottom: "10px" }}>
                Apka apna Tax Consultant!
              </div>
              <div className="company-details" style={{ fontSize: "12px", color: "#4b5563", marginBottom: "10px" }}>
                <div style={{ fontWeight: "600" }}>MANDET INDIA PVT LTD</div>
                <div>Fl 07, Shefali Appt, Off Karve Rd, Erandwane, Pune - 411004</div>
                <div>020 - 29980420 | 7276016790</div>
                <div>apka@taxsarthi.com | taxsarthi.com</div>
              </div>
              <div style={{ fontSize: "10px", color: "#6b7280" }}>
                <div>CIN: U80100PN2021PTC198977 | ISO 9001 : 2015 Certified & MCA Registered | STARTUP INDIA CERTIFIED</div>
                <div>Recognised by STARTUP INDIA</div>
              </div>
            </div>

            {/* Basic Information */}
            <div className="basic-info" style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "10px", marginBottom: "20px", fontSize: "13px" }}>
              <div><strong>PAN:</strong> {formData.pan}</div>
              <div><strong>NAME:</strong> {formData.name}</div>
              <div><strong>AADHAAR:</strong> {formData.aadhaar}</div>
              <div><strong>FINANCIAL YEAR:</strong> {formData.financialYear}</div>
              <div><strong>BANK A/C NO.:</strong> {formData.bankAccount}</div>
              <div><strong>ASSESSMENT YEAR:</strong> {formData.assessmentYear}</div>
              <div><strong>IFSC:</strong> {formData.ifsc}</div>
              <div><strong>TAX REGIME BENEFICIAL:</strong> {formData.taxRegime}</div>
            </div>

            <h2 style={{ fontSize: "20px", fontWeight: "bold", textAlign: "center", marginBottom: "20px", color: "#1f2937" }}>
              COMPUTATION OF TAXABLE INCOME
            </h2>

            {/* Income from Salary */}
            {formData.showSalary && (
              <div style={{ marginBottom: "20px" }}>
                <h3 style={{ fontSize: "16px", fontWeight: "bold", backgroundColor: "#dbeafe", padding: "8px", marginBottom: "10px", color: "#1e40af" }}>
                  INCOME FROM SALARY
                </h3>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
                  <thead>
                    <tr style={{ backgroundColor: "#f3f4f6" }}>
                      <th style={{ border: "1px solid #374151", padding: "8px", textAlign: "left" }}>CALCULATION</th>
                      <th style={{ border: "1px solid #374151", padding: "8px", textAlign: "center" }}>OLD TAX REGIME</th>
                      <th style={{ border: "1px solid #374151", padding: "8px", textAlign: "center" }}>NEW TAX REGIME</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ border: "1px solid #374151", padding: "6px" }}>Gross Salary</td>
                      <td style={{ border: "1px solid #374151", padding: "6px", textAlign: "right" }}>₹{formData.grossSalary.toLocaleString("en-IN")}</td>
                      <td style={{ border: "1px solid #374151", padding: "6px", textAlign: "right" }}>₹{formData.grossSalary.toLocaleString("en-IN")}</td>
                    </tr>
                    <tr>
                      <td style={{ border: "1px solid #374151", padding: "6px" }}>Less: HRA</td>
                      <td style={{ border: "1px solid #374151", padding: "6px", textAlign: "right" }}>₹{formData.hra.toLocaleString("en-IN")}</td>
                      <td style={{ border: "1px solid #374151", padding: "6px", textAlign: "right" }}>₹{formData.hra.toLocaleString("en-IN")}</td>
                    </tr>
                    <tr>
                      <td style={{ border: "1px solid #374151", padding: "6px" }}>Less: Travel Allowance</td>
                      <td style={{ border: "1px solid #374151", padding: "6px", textAlign: "right" }}>₹{formData.travelAllowance.toLocaleString("en-IN")}</td>
                      <td style={{ border: "1px solid #374151", padding: "6px", textAlign: "right" }}>₹{formData.travelAllowance.toLocaleString("en-IN")}</td>
                    </tr>
                    <tr>
                      <td style={{ border: "1px solid #374151", padding: "6px" }}>Less: Uniform Allowance</td>
                      <td style={{ border: "1px solid #374151", padding: "6px", textAlign: "right" }}>₹{formData.uniformAllowance.toLocaleString("en-IN")}</td>
                      <td style={{ border: "1px solid #374151", padding: "6px", textAlign: "right" }}>₹{formData.uniformAllowance.toLocaleString("en-IN")}</td>
                    </tr>
                    <tr>
                      <td style={{ border: "1px solid #374151", padding: "6px" }}>Less: Other Allowance</td>
                      <td style={{ border: "1px solid #374151", padding: "6px", textAlign: "right" }}>₹{formData.otherAllowance.toLocaleString("en-IN")}</td>
                      <td style={{ border: "1px solid #374151", padding: "6px", textAlign: "right" }}>₹{formData.otherAllowance.toLocaleString("en-IN")}</td>
                    </tr>
                    <tr>
                      <td style={{ border: "1px solid #374151", padding: "6px" }}>Less: Standard Deduction</td>
                      <td style={{ border: "1px solid #374151", padding: "6px", textAlign: "right" }}>₹{formData.standardDeduction.toLocaleString("en-IN")}</td>
                      <td style={{ border: "1px solid #374151", padding: "6px", textAlign: "right" }}>₹{formData.standardDeduction.toLocaleString("en-IN")}</td>
                    </tr>
                    <tr>
                      <td style={{ border: "1px solid #374151", padding: "6px" }}>Less: Professional Tax</td>
                      <td style={{ border: "1px solid #374151", padding: "6px", textAlign: "right" }}>₹{formData.professionalTax.toLocaleString("en-IN")}</td>
                      <td style={{ border: "1px solid #374151", padding: "6px", textAlign: "right" }}>₹{formData.professionalTax.toLocaleString("en-IN")}</td>
                    </tr>
                    <tr style={{ backgroundColor: "#dbeafe", fontWeight: "bold" }}>
                      <td style={{ border: "1px solid #374151", padding: "8px" }}>TOTAL</td>
                      <td style={{ border: "1px solid #374151", padding: "8px", textAlign: "right" }}>₹{calculateSalaryIncome().toLocaleString("en-IN")}</td>
                      <td style={{ border: "1px solid #374151", padding: "8px", textAlign: "right" }}>₹{calculateSalaryIncome().toLocaleString("en-IN")}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}

            {/* Income from Other Sources */}
            {formData.showOtherSources && (
              <div style={{ marginBottom: "20px" }}>
                <h3 style={{ fontSize: "16px", fontWeight: "bold", backgroundColor: "#fef3c7", padding: "8px", marginBottom: "10px", color: "#92400e" }}>
                  INCOME FROM OTHER SOURCES
                </h3>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
                  <thead>
                    <tr style={{ backgroundColor: "#f3f4f6" }}>
                      <th style={{ border: "1px solid #374151", padding: "8px", textAlign: "left" }}>CALCULATION</th>
                      <th style={{ border: "1px solid #374151", padding: "8px", textAlign: "center" }}>OLD TAX REGIME</th>
                      <th style={{ border: "1px solid #374151", padding: "8px", textAlign: "center" }}>NEW TAX REGIME</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ border: "1px solid #374151", padding: "6px" }}>Savings Bank Interest</td>
                      <td style={{ border: "1px solid #374151", padding: "6px", textAlign: "right" }}>₹{formData.savingsBankInterest.toLocaleString("en-IN")}</td>
                      <td style={{ border: "1px solid #374151", padding: "6px", textAlign: "right" }}>₹{formData.savingsBankInterest.toLocaleString("en-IN")}</td>
                    </tr>
                    <tr>
                      <td style={{ border: "1px solid #374151", padding: "6px" }}>Term Bank Deposit Interest</td>
                      <td style={{ border: "1px solid #374151", padding: "6px", textAlign: "right" }}>₹{formData.termDepositInterest.toLocaleString("en-IN")}</td>
                      <td style={{ border: "1px solid #374151", padding: "6px", textAlign: "right" }}>₹{formData.termDepositInterest.toLocaleString("en-IN")}</td>
                    </tr>
                    <tr>
                      <td style={{ border: "1px solid #374151", padding: "6px" }}>NSC Interest for the Year</td>
                      <td style={{ border: "1px solid #374151", padding: "6px", textAlign: "right" }}>₹{formData.nscInterest.toLocaleString("en-IN")}</td>
                      <td style={{ border: "1px solid #374151", padding: "6px", textAlign: "right" }}>₹{formData.nscInterest.toLocaleString("en-IN")}</td>
                    </tr>
                    <tr>
                      <td style={{ border: "1px solid #374151", padding: "6px" }}>Dividend & Other</td>
                      <td style={{ border: "1px solid #374151", padding: "6px", textAlign: "right" }}>₹{formData.dividend.toLocaleString("en-IN")}</td>
                      <td style={{ border: "1px solid #374151", padding: "6px", textAlign: "right" }}>₹{formData.dividend.toLocaleString("en-IN")}</td>
                    </tr>
                    <tr>
                      <td style={{ border: "1px solid #374151", padding: "6px" }}>Other Income</td>
                      <td style={{ border: "1px solid #374151", padding: "6px", textAlign: "right" }}>₹{formData.otherIncome.toLocaleString("en-IN")}</td>
                      <td style={{ border: "1px solid #374151", padding: "6px", textAlign: "right" }}>₹{formData.otherIncome.toLocaleString("en-IN")}</td>
                    </tr>
                    <tr style={{ backgroundColor: "#fef3c7", fontWeight: "bold" }}>
                      <td style={{ border: "1px solid #374151", padding: "8px" }}>TOTAL</td>
                      <td style={{ border: "1px solid #374151", padding: "8px", textAlign: "right" }}>₹{calculateOtherSources().toLocaleString("en-IN")}</td>
                      <td style={{ border: "1px solid #374151", padding: "8px", textAlign: "right" }}>₹{calculateOtherSources().toLocaleString("en-IN")}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}

            {/* Gross Total Taxable Income */}
            <div style={{ marginBottom: "20px" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
                <tbody>
                  <tr style={{ backgroundColor: "#e5e7eb", fontWeight: "bold" }}>
                    <td style={{ border: "1px solid #374151", padding: "12px" }}>GROSS TOTAL TAXABLE INCOME</td>
                    <td style={{ border: "1px solid #374151", padding: "12px", textAlign: "right" }}>₹{calculateGrossTotalIncome().toLocaleString("en-IN")}</td>
                    <td style={{ border: "1px solid #374151", padding: "12px", textAlign: "right" }}>₹{calculateGrossTotalIncome().toLocaleString("en-IN")}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Deductions Section */}
            <div style={{ marginBottom: "20px" }}>
              <h3 style={{ fontSize: "16px", fontWeight: "bold", backgroundColor: "#e0e7ff", padding: "8px", marginBottom: "10px", color: "#3730a3" }}>
                LESS: DEDUCTIONS
              </h3>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
                <thead>
                  <tr style={{ backgroundColor: "#f3f4f6" }}>
                    <th style={{ border: "1px solid #374151", padding: "8px", textAlign: "left" }}>CALCULATION</th>
                    <th style={{ border: "1px solid #374151", padding: "8px", textAlign: "center" }}>OLD TAX REGIME</th>
                    <th style={{ border: "1px solid #374151", padding: "8px", textAlign: "center" }}>NEW TAX REGIME</th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ backgroundColor: "#e0e7ff" }}>
                    <td style={{ border: "1px solid #374151", padding: "6px", fontWeight: "bold" }} colSpan="3">Sec 80C/80CCC/80CCD(1)</td>
                  </tr>
                  <tr>
                    <td style={{ border: "1px solid #374151", padding: "6px" }}>Life Insurance</td>
                    <td style={{ border: "1px solid #374151", padding: "6px", textAlign: "right" }}>₹{formData.lifeInsurance.toLocaleString("en-IN")}</td>
                    <td style={{ border: "1px solid #374151", padding: "6px", textAlign: "right" }}>-</td>
                  </tr>
                  <tr>
                    <td style={{ border: "1px solid #374151", padding: "6px" }}>Provident Fund</td>
                    <td style={{ border: "1px solid #374151", padding: "6px", textAlign: "right" }}>₹{formData.providentFund.toLocaleString("en-IN")}</td>
                    <td style={{ border: "1px solid #374151", padding: "6px", textAlign: "right" }}>-</td>
                  </tr>
                  <tr>
                    <td style={{ border: "1px solid #374151", padding: "6px" }}>Public Provident Fund</td>
                    <td style={{ border: "1px solid #374151", padding: "6px", textAlign: "right" }}>₹{formData.ppf.toLocaleString("en-IN")}</td>
                    <td style={{ border: "1px solid #374151", padding: "6px", textAlign: "right" }}>-</td>
                  </tr>
                  <tr>
                    <td style={{ border: "1px solid #374151", padding: "6px" }}>Housing Loan Principal Repayment</td>
                    <td style={{ border: "1px solid #374151", padding: "6px", textAlign: "right" }}>₹{formData.housingLoanPrincipal.toLocaleString("en-IN")}</td>
                    <td style={{ border: "1px solid #374151", padding: "6px", textAlign: "right" }}>-</td>
                  </tr>
                  <tr>
                    <td style={{ border: "1px solid #374151", padding: "6px" }}>NSC Investment + Accrued Interest</td>
                    <td style={{ border: "1px solid #374151", padding: "6px", textAlign: "right" }}>₹{formData.nscInvestment.toLocaleString("en-IN")}</td>
                    <td style={{ border: "1px solid #374151", padding: "6px", textAlign: "right" }}>-</td>
                  </tr>
                  <tr>
                    <td style={{ border: "1px solid #374151", padding: "6px" }}>80CCC: Annuity plan of LIC or other issuer towards Pension Scheme</td>
                    <td style={{ border: "1px solid #374151", padding: "6px", textAlign: "right" }}>₹{formData.annuityPlan.toLocaleString("en-IN")}</td>
                    <td style={{ border: "1px solid #374151", padding: "6px", textAlign: "right" }}>-</td>
                  </tr>
                  <tr>
                    <td style={{ border: "1px solid #374151", padding: "6px" }}>80CCD(1): Pension Scheme of Central Government</td>
                    <td style={{ border: "1px solid #374151", padding: "6px", textAlign: "right" }}>₹{formData.pensionScheme.toLocaleString("en-IN")}</td>
                    <td style={{ border: "1px solid #374151", padding: "6px", textAlign: "right" }}>-</td>
                  </tr>
                  <tr>
                    <td style={{ border: "1px solid #374151", padding: "6px" }}>80CCD(1B): Self Contribution in NPS</td>
                    <td style={{ border: "1px solid #374151", padding: "6px", textAlign: "right" }}>₹{formData.npsContribution.toLocaleString("en-IN")}</td>
                    <td style={{ border: "1px solid #374151", padding: "6px", textAlign: "right" }}>-</td>
                  </tr>
                  <tr>
                    <td style={{ border: "1px solid #374151", padding: "6px" }}>80CCD(2): Pension Scheme of Central Government</td>
                    <td style={{ border: "1px solid #374151", padding: "6px", textAlign: "right" }}>₹{formData.npsEmployer.toLocaleString("en-IN")}</td>
                    <td style={{ border: "1px solid #374151", padding: "6px", textAlign: "right" }}>-</td>
                  </tr>
                  <tr>
                    <td style={{ border: "1px solid #374151", padding: "6px" }}>80D: Medical & Preventive Check-up</td>
                    <td style={{ border: "1px solid #374151", padding: "6px", textAlign: "right" }}>₹{formData.medicalInsurance.toLocaleString("en-IN")}</td>
                    <td style={{ border: "1px solid #374151", padding: "6px", textAlign: "right" }}>-</td>
                  </tr>
                  <tr>
                    <td style={{ border: "1px solid #374151", padding: "6px" }}>80DD: Medical Treatment of Disabled Dependent</td>
                    <td style={{ border: "1px solid #374151", padding: "6px", textAlign: "right" }}>₹{formData.disabledDependent.toLocaleString("en-IN")}</td>
                    <td style={{ border: "1px solid #374151", padding: "6px", textAlign: "right" }}>-</td>
                  </tr>
                  <tr>
                    <td style={{ border: "1px solid #374151", padding: "6px" }}>80DDB: Medical Treatment for Specified Disease</td>
                    <td style={{ border: "1px solid #374151", padding: "6px", textAlign: "right" }}>₹{formData.specifiedDisease.toLocaleString("en-IN")}</td>
                    <td style={{ border: "1px solid #374151", padding: "6px", textAlign: "right" }}>-</td>
                  </tr>
                  <tr>
                    <td style={{ border: "1px solid #374151", padding: "6px" }}>80G: Donation</td>
                    <td style={{ border: "1px solid #374151", padding: "6px", textAlign: "right" }}>₹{formData.donation.toLocaleString("en-IN")}</td>
                    <td style={{ border: "1px solid #374151", padding: "6px", textAlign: "right" }}>-</td>
                  </tr>
                  <tr>
                    <td style={{ border: "1px solid #374151", padding: "6px" }}>80GG: Deduction towards House Rent</td>
                    <td style={{ border: "1px solid #374151", padding: "6px", textAlign: "right" }}>₹{formData.houseRent.toLocaleString("en-IN")}</td>
                    <td style={{ border: "1px solid #374151", padding: "6px", textAlign: "right" }}>-</td>
                  </tr>
                  <tr>
                    <td style={{ border: "1px solid #374151", padding: "6px" }}>80TTA: Interest on Savings Accounts</td>
                    <td style={{ border: "1px solid #374151", padding: "6px", textAlign: "right" }}>₹{formData.savingsInterest.toLocaleString("en-IN")}</td>
                    <td style={{ border: "1px solid #374151", padding: "6px", textAlign: "right" }}>-</td>
                  </tr>
                  <tr>
                    <td style={{ border: "1px solid #374151", padding: "6px" }}>80U: Person with Disability</td>
                    <td style={{ border: "1px solid #374151", padding: "6px", textAlign: "right" }}>₹{formData.disability.toLocaleString("en-IN")}</td>
                    <td style={{ border: "1px solid #374151", padding: "6px", textAlign: "right" }}>-</td>
                  </tr>
                  <tr>
                    <td style={{ border: "1px solid #374151", padding: "6px" }}>Any Other Deduction</td>
                    <td style={{ border: "1px solid #374151", padding: "6px", textAlign: "right" }}>₹{formData.otherDeduction.toLocaleString("en-IN")}</td>
                    <td style={{ border: "1px solid #374151", padding: "6px", textAlign: "right" }}>-</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Final Tax Calculation */}
            <div style={{ marginBottom: "30px" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                <tbody>
                  <tr style={{ backgroundColor: "#dbeafe", fontWeight: "bold", fontSize: "14px" }}>
                    <td style={{ border: "1px solid #374151", padding: "10px" }}>TOTAL TAXABLE INCOME</td>
                    <td style={{ border: "1px solid #374151", padding: "10px", textAlign: "right" }}>₹{calculateTaxableIncome().toLocaleString("en-IN")}</td>
                    <td style={{ border: "1px solid #374151", padding: "10px", textAlign: "right" }}>₹{calculateTaxableIncome().toLocaleString("en-IN")}</td>
                  </tr>
                  <tr style={{ backgroundColor: "#fee2e2", fontWeight: "bold" }}>
                    <td style={{ border: "1px solid #374151", padding: "10px" }}>TAX ON TOTAL TAXABLE INCOME</td>
                    <td style={{ border: "1px solid #374151", padding: "10px", textAlign: "right" }}>₹{calculateTax().toLocaleString("en-IN")}</td>
                    <td style={{ border: "1px solid #374151", padding: "10px", textAlign: "right" }}>₹{calculateTax().toLocaleString("en-IN")}</td>
                  </tr>
                  <tr>
                    <td style={{ border: "1px solid #374151", padding: "8px" }}>LESS: REBATE U/S 87A</td>
                    <td style={{ border: "1px solid #374151", padding: "8px", textAlign: "right" }}>₹{calculateRebateU87A().toLocaleString("en-IN")}</td>
                    <td style={{ border: "1px solid #374151", padding: "8px", textAlign: "right" }}>₹{calculateRebateU87A().toLocaleString("en-IN")}</td>
                  </tr>
                  <tr>
                    <td style={{ border: "1px solid #374151", padding: "8px" }}>TAX PAYABLE AFTER REBATE</td>
                    <td style={{ border: "1px solid #374151", padding: "8px", textAlign: "right" }}>₹{Math.max(0, calculateTax() - calculateRebateU87A()).toLocaleString("en-IN")}</td>
                    <td style={{ border: "1px solid #374151", padding: "8px", textAlign: "right" }}>₹{Math.max(0, calculateTax() - calculateRebateU87A()).toLocaleString("en-IN")}</td>
                  </tr>
                  <tr>
                    <td style={{ border: "1px solid #374151", padding: "8px" }}>EDUCATION CESS</td>
                    <td style={{ border: "1px solid #374151", padding: "8px", textAlign: "right" }}>₹{calculateEducationCess().toLocaleString("en-IN")}</td>
                    <td style={{ border: "1px solid #374151", padding: "8px", textAlign: "right" }}>₹{calculateEducationCess().toLocaleString("en-IN")}</td>
                  </tr>
                  <tr>
                    <td style={{ border: "1px solid #374151", padding: "8px" }}>LESS: TDS/TCS</td>
                    <td style={{ border: "1px solid #374151", padding: "8px", textAlign: "right" }}>₹{(formData.tds + formData.tcs).toLocaleString("en-IN")}</td>
                    <td style={{ border: "1px solid #374151", padding: "8px", textAlign: "right" }}>₹{(formData.tds + formData.tcs).toLocaleString("en-IN")}</td>
                  </tr>
                  <tr style={{ backgroundColor: "#fecaca", fontWeight: "bold", fontSize: "15px" }}>
                    <td style={{ border: "2px solid #374151", padding: "12px" }}>BALANCE TAX PAYABLE/REFUND</td>
                    <td style={{ border: "2px solid #374151", padding: "12px", textAlign: "right", color: calculateNetTaxLiability() >= 0 ? "#dc2626" : "#059669" }}>
                      {calculateNetTaxLiability() >= 0 ? "₹" : "-₹"}{Math.abs(calculateNetTaxLiability()).toLocaleString("en-IN")}
                      <br />
                      <span style={{ fontSize: "12px", fontWeight: "normal" }}>
                        {calculateNetTaxLiability() >= 0 ? "(Payable)" : "(Refund)"}
                      </span>
                    </td>
                    <td style={{ border: "2px solid #374151", padding: "12px", textAlign: "right", color: calculateNetTaxLiability() >= 0 ? "#dc2626" : "#059669" }}>
                      {calculateNetTaxLiability() >= 0 ? "₹" : "-₹"}{Math.abs(calculateNetTaxLiability()).toLocaleString("en-IN")}
                      <br />
                      <span style={{ fontSize: "12px", fontWeight: "normal" }}>
                        {calculateNetTaxLiability() >= 0 ? "(Payable)" : "(Refund)"}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Declaration & Confirmation */}
            <div style={{ marginTop: "40px", borderTop: "2px solid #6b7280", paddingTop: "20px" }}>
              <h3 style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "15px" }}>Declaration & Confirmation</h3>
              <div style={{ backgroundColor: "#f9fafb", padding: "20px", border: "1px solid #d1d5db", borderRadius: "8px" }}>
                <p style={{ fontSize: "12px", marginBottom: "20px", lineHeight: "1.6" }}>
                  I <strong>{formData.name}</strong>, PAN: <strong>{formData.pan}</strong> hereby confirm that I
                  have reviewed the details of the computation of taxable income as mentioned in this
                  document. To the best of my knowledge and belief, the information provided herein is true,
                  correct, and complete. I authorize Tax Sarthi to proceed with the filing of my Income Tax
                  Return based on this computation.
                </p>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "30px", marginTop: "25px" }}>
                  <div>
                    <p style={{ fontWeight: "bold", marginBottom: "8px" }}>Name:</p>
                    <div style={{ borderBottom: "2px solid #374151", height: "30px", display: "flex", alignItems: "end", paddingBottom: "2px" }}>
                      <span style={{ fontSize: "11px", color: "#6b7280" }}>{formData.name}</span>
                    </div>
                  </div>
                  <div>
                    <p style={{ fontWeight: "bold", marginBottom: "8px" }}>PAN:</p>
                    <div style={{ borderBottom: "2px solid #374151", height: "30px", display: "flex", alignItems: "end", paddingBottom: "2px" }}>
                      <span style={{ fontSize: "11px", color: "#6b7280" }}>{formData.pan}</span>
                    </div>
                  </div>
                  <div>
                    <p style={{ fontWeight: "bold", marginBottom: "8px" }}>Date:</p>
                    <div style={{ borderBottom: "2px solid #374151", height: "30px", display: "flex", alignItems: "end", paddingBottom: "2px" }}>
                      <span style={{ fontSize: "11px", color: "#6b7280" }}>{new Date().toLocaleDateString("en-IN")}</span>
                    </div>
                  </div>
                </div>
                <div style={{ marginTop: "25px", width: "50%" }}>
                  <p style={{ fontWeight: "bold", marginBottom: "8px" }}>Signature:</p>
                  <div style={{ borderBottom: "2px solid #374151", height: "40px" }}></div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div style={{ marginTop: "30px", textAlign: "center", fontSize: "10px", color: "#6b7280", borderTop: "1px solid #d1d5db", paddingTop: "15px" }}>
              <div style={{ marginBottom: "5px" }}>
                <strong>CIN: U80100PN2021PTC198977 | ISO 9001 : 2015 Certified & MCA Registered | STARTUP INDIA CERTIFIED</strong>
              </div>
              <p>This document is generated by Tax Sarthi on {new Date().toLocaleDateString("en-IN")}</p>
              <p>This is a computer generated document and does not require signature</p>
              <p style={{ marginTop: "8px" }}>For any queries, contact: apka@taxsarthi.com | 7276016790</p>
            </div>
          </div>
        </div>

        {/* Summary Card */}
        <div className="mt-6 bg-white p-6 rounded-lg shadow-lg">
          <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Tax Computation Summary
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-sm text-blue-600 font-medium">Gross Total Income</p>
              <p className="text-xl font-bold text-blue-800">₹{calculateGrossTotalIncome().toLocaleString("en-IN")}</p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <p className="text-sm text-green-600 font-medium">Total Deductions</p>
              <p className="text-xl font-bold text-green-800">₹{calculateTotalDeductions().toLocaleString("en-IN")}</p>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg">
              <p className="text-sm text-purple-600 font-medium">Taxable Income</p>
              <p className="text-xl font-bold text-purple-800">₹{calculateTaxableIncome().toLocaleString("en-IN")}</p>
            </div>
            <div className={`p-4 rounded-lg ${calculateNetTaxLiability() >= 0 ? "bg-red-50" : "bg-green-50"}`}>
              <p className={`text-sm font-medium ${calculateNetTaxLiability() >= 0 ? "text-red-600" : "text-green-600"}`}>
                Net {calculateNetTaxLiability() >= 0 ? "Tax Payable" : "Refund"}
              </p>
              <p className={`text-xl font-bold ${calculateNetTaxLiability() >= 0 ? "text-red-800" : "text-green-800"}`}>
                ₹{Math.abs(calculateNetTaxLiability()).toLocaleString("en-IN")}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

TaxComputationPDF.propTypes = {
  pan: PropTypes.string.isRequired,
  assessmentYear: PropTypes.string.isRequired,
  regime: PropTypes.oneOf(["old", "new"]),
  userData: PropTypes.shape({
    name: PropTypes.string,
    aadhaar: PropTypes.string,
    bankAccount: PropTypes.string,
    ifsc: PropTypes.string,
    incomeFromSalary: PropTypes.object,
    incomeFromHouseProperty: PropTypes.object,
    incomeFromCapitalGain: PropTypes.object,
    incomeFromBusiness: PropTypes.object,
    incomeFromOtherSources: PropTypes.object,
    deductions: PropTypes.object,
    taxDetails: PropTypes.object,
  }),
};

TaxComputationPDF.defaultProps = {
  regime: "old",
  userData: {},
};

export default TaxComputationPDF;